import { useState } from "react";
import { useWorker } from "../context/WorkerContext";

const BASE_URL = process.env.REACT_APP_BASE_URL;

/**
 * All API logic for the Aadhaar signup step.
 *  - uploadSide(file, "front" | "back") → { name, dob, address, aadhaarNo }
 *    (sends isEdit=true only when editing)
 *  - saveDetails({ dob, address })      → saves details (+ resets agreement when editing)
 * Both throw an Error with a user-friendly message on failure.
 */
const useAadhaarUpload = (isEditing = false) => {
  const { user, setUser, token } = useWorker();
  const [isUploading, setIsUploading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const uploadSide = async (file, side) => {
    const workerId = user?.id || user?._id;
    if (!workerId) throw new Error("Worker ID not found");

    const formData = new FormData();
    formData.append("workerId", workerId);
    formData.append("side", side);
    formData.append("aadhaar_image", file);
    if (isEditing) formData.append("isEdit", "true"); // only sent when editing

    setIsUploading(true);
    try {
      const res = await fetch(`${BASE_URL}/lenskart/upload-aadhaar-image`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` }, // no Content-Type: browser sets multipart boundary
        body: formData,
      });
      const data = await res.json().catch(() => ({}));

      if (!res.ok || data.success === false) {
        throw new Error(
          data.message || data.error || "Could not read Aadhaar image. Try again."
        );
      }

      return {
        name: data.name || null,
        dob: data.dob || null,
        address: data.address || null,
        aadhaarNo: data.aadhaarNo || null,
      };
    } finally {
      setIsUploading(false);
    }
  };

  const saveDetails = async ({ dob, address }) => {
    const cleanAddress = address.trim();
    setIsSaving(true);
    try {
      const res = await fetch(`${BASE_URL}/lenskart/details/update`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ dob, address: cleanAddress }),
      });
      if (!res.ok) throw new Error("Failed to update details");

      if (isEditing) {
        const agreementRes = await fetch(`${BASE_URL}/lenskart/mark-agreement`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ userId: user.id, isAgreementSigned: false }),
        });
        if (!agreementRes.ok) throw new Error("Failed to update agreement status");
      }

      setUser((prev) => ({ ...prev, dob, address: cleanAddress, ...(isEditing && { isAgreementSigned: false }),}));
    } finally {
      setIsSaving(false);
    }
  };

  return { uploadSide, saveDetails, isUploading, isSaving };
};

export default useAadhaarUpload;